import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useRentabilityCalculator from '../../hooks/useRentabilityCalculator';
import BuildingDashboard from '../BuildingDashboard';
import PropertyReport from '../PropertyReport';
import AmortizationPage from '../AmortizationPage';
import FinancingScenarioForm from '../FinancingScenarioForm';
import ProjectDocumentsPage from '../projectDocuments/ProjectDocumentsPage';
import defaultProperty from '../../defaults/defaultProperty';
import {
  saveShareScenario,
  updateShareScenario,
  addSharedWithMe,
  getSharedWithMeOnce,
  getPocketClientIdFromLocationHash,
  subscribeShare,
} from '../../services/shareService';
import { linkPocketDealRecipient, pocketLinkErrorReason } from '../../services/pocketDealLinkService';
import { interestUnlocksSharedAddress } from '../../utils/shareRecipientAddressVisibility';
import { isOwnedDeal } from '../../utils/dealPurpose';
import { useRegisterAppBack } from '../../hooks/useAppBackButton';
import { useScrollToTopOnChange } from '../../hooks/useScrollToTopOnChange';

const lockedFields = { debtCoverage: true, welcomeTax: true };

/**
 * View of a shared property + scenarios (token-based share).
 * When access === 'write', allows creating and editing scenarios (stored in share subcollection).
 */
const SharedPropertyView = ({
  shareData: shareDataProp,
  openScenario = null,
  onBack = null,
  onOpenDealFromChat = null,
  onRemoveFromSharedDashboard = null,
}) => {
  const { currentUser, refreshSharedWithMe, refreshUserProfile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [liveShareData, setLiveShareData] = useState(null);
  const shareData = liveShareData || shareDataProp;

  const handleDealFromChat = (payload) => {
    if (onOpenDealFromChat) {
      onOpenDealFromChat(payload);
      return;
    }
    if (payload.type === 'shared' && payload.shareToken) {
      navigate({ pathname: '/', search: `?share=${encodeURIComponent(payload.shareToken)}` });
    } else {
      navigate('/');
    }
  };
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [viewMode, setViewMode] = useState('dashboard');
  useScrollToTopOnChange(viewMode);
  const [editingScenario, setEditingScenario] = useState(null);
  const [amortizationData, setAmortizationData] = useState(null);
  const [pocketDealLinkEmailMismatch, setPocketDealLinkEmailMismatch] = useState(false);
  const [recipientInterestForAddress, setRecipientInterestForAddress] = useState(null);
  const addedToDashboardRef = useRef(false);
  const shareToken = shareData?.id || shareDataProp?.id || null;

  useEffect(() => {
    const token = shareDataProp?.id;
    if (!token) {
      setLiveShareData(null);
      return undefined;
    }
    return subscribeShare(token, (data) => {
      if (data) setLiveShareData(data);
    });
  }, [shareDataProp?.id]);

  useEffect(() => {
    if (!shareToken || !currentUser?.uid || !shareData?.anonymizeAddress) {
      setRecipientInterestForAddress(null);
      return undefined;
    }
    const q = query(
      collection(firestore, 'shares', shareToken, 'interests'),
      where('recipientUid', '==', currentUser.uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setRecipientInterestForAddress(null);
          return;
        }
        const d = snap.docs[0];
        setRecipientInterestForAddress({ id: d.id, ...d.data() });
      },
      () => setRecipientInterestForAddress(null),
    );
    return () => unsub();
  }, [shareToken, currentUser?.uid, shareData?.anonymizeAddress]);

  useEffect(() => {
    if (openScenario && shareData?.id) {
      setSelectedScenario(openScenario);
      setViewMode('report');
    }
  }, [openScenario?.id, shareData?.id]);

  // When user is logged in and viewing a share link, add it to their "Partagés avec moi" once (if not already there).
  useEffect(() => {
    if (!currentUser?.uid || !shareToken || !shareData?.pocketDealMode) return;
    const pcid = getPocketClientIdFromLocationHash();
    if (!pcid) return;
    let cancelled = false;
    setPocketDealLinkEmailMismatch(false);
    linkPocketDealRecipient({ shareToken, clientId: pcid })
      .then(() => {
        if (!cancelled) refreshUserProfile?.().catch(() => {});
      })
      .catch((e) => {
        const reason = pocketLinkErrorReason(e);
        console.warn('SharedPropertyView: linkPocketDealRecipient', e?.code, e?.message, e?.details ?? e?.customData);
        if (!cancelled && reason === 'email_mismatch') setPocketDealLinkEmailMismatch(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, shareToken, shareData?.pocketDealMode, refreshUserProfile]);

  useEffect(() => {
    if (!currentUser?.uid || !shareData?.id || !shareData?.uid || !shareData?.snapshot || addedToDashboardRef.current) return;
    let cancelled = false;
    getSharedWithMeOnce(currentUser.uid)
      .then((items) => {
        if (cancelled) return;
        const alreadyHas = items.some((item) => item.shareToken === shareData.id);
        if (alreadyHas) {
          addedToDashboardRef.current = true;
          return;
        }
        return addSharedWithMe(currentUser.uid, shareData.uid, shareData.id, shareData.snapshot, { anonymizeAddress: shareData.anonymizeAddress }).then(() => {
          if (!cancelled) {
            addedToDashboardRef.current = true;
            refreshSharedWithMe?.();
            navigate('/', { state: { shareAdded: true }, replace: true });
          }
        });
      })
      .catch((e) => console.warn('SharedPropertyView: addSharedWithMe', e));
    return () => { cancelled = true; };
  }, [currentUser?.uid, shareData?.id, shareData?.uid, shareData?.snapshot, refreshSharedWithMe]);

  const snapshot = shareData?.snapshot || {};
  const rawProperty = snapshot.property || defaultProperty;
  const shareAnonymizesAddress = !!shareData?.anonymizeAddress;
  const interestStatus = recipientInterestForAddress?.status;
  const recipientMaySeeFullAddress = interestUnlocksSharedAddress(interestStatus);
  const shouldMaskAddress = shareAnonymizesAddress && !recipientMaySeeFullAddress;
  const property = shouldMaskAddress
    ? { ...rawProperty, address: '**** *************', postalCode: '*** ***' }
    : rawProperty;
  const baseScenarios = snapshot.scenarios || [];
  const access = (shareData?.access || 'read').toLowerCase();
  const allowSubScenariosEdit = shareData?.allowSubScenariosEdit === true;
  const ownedShareLocked =
    shareData?.liveSync === true ||
    shareData?.dealPurpose === 'owned' ||
    isOwnedDeal(rawProperty);
  const hasWriteAccess = !ownedShareLocked && access === 'write';
  const isWrite = hasWriteAccess && !!currentUser;

  const bannerText = isWrite
    ? t('sharedView.badgeWrite')
    : hasWriteAccess && !currentUser
      ? t('sharedView.loginToEdit')
      : t('sharedView.badge');

  /** Pocket « Voir deal » : pas de bandeau lecture seule / lecture-écriture dans l’UI */
  const showShareAccessBanner = !!shareToken && !shareData?.pocketDealMode;

  const [advancedExpenses] = useState(property?.advancedExpenses ?? false);
  const analysis = useRentabilityCalculator(
    property,
    advancedExpenses,
    lockedFields,
    () => {},
  );

  const propertyWithAnalysis = { ...property, ...analysis, id: shareToken };
  const creatorInfo = currentUser
    ? { createdByUid: currentUser.uid, createdByName: currentUser.displayName || currentUser.email || currentUser.uid }
    : null;

  const pocketClientIdForBanner =
    typeof window !== 'undefined' ? getPocketClientIdFromLocationHash() : null;
  const pocketAuthQuerySuffix = pocketClientIdForBanner
    ? `&pc=${encodeURIComponent(pocketClientIdForBanner)}`
    : '';

  const isCessionRevoked = !!(
    shareData?.cessionCompletedAt &&
    (currentUser?.uid !== shareData?.cessionnaireUid || !currentUser)
  );
  useRegisterAppBack(() => {
    if (viewMode !== 'dashboard') {
      setViewMode('dashboard');
      setEditingScenario(null);
      setSelectedScenario(null);
      setAmortizationData(null);
      return;
    }
    onBack?.();
  }, Boolean(onBack));
  if (isCessionRevoked) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center justify-center">
        <p className="text-lg text-slate-700 mb-4 text-center">{t('home.cessionRevoked')}</p>
        {onBack ? (
          <button type="button" onClick={onBack} className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700">
            {t('back')}
          </button>
        ) : (
          <Link to="/" className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700">
            {t('back')}
          </Link>
        )}
      </div>
    );
  }

  if (viewMode === 'create') {
    return (
      <div className="min-h-screen bg-gray-50">
        {showShareAccessBanner ? (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-amber-800 text-sm font-medium break-words">{bannerText}</span>
          </div>
        ) : null}
        <FinancingScenarioForm
          propertyId={shareToken}
          property={propertyWithAnalysis}
          advancedExpenses={advancedExpenses}
          onBack={() => setViewMode('dashboard')}
          onSaved={() => setViewMode('dashboard')}
          onSaveScenario={(data) => saveShareScenario(shareToken, data, creatorInfo)}
          onUpdateScenario={(id, data) => updateShareScenario(shareToken, id, data)}
        />
      </div>
    );
  }

  if (viewMode === 'edit' && editingScenario) {
    return (
      <div className="min-h-screen bg-gray-50">
        {showShareAccessBanner ? (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-amber-800 text-sm font-medium break-words">{bannerText}</span>
          </div>
        ) : null}
        <FinancingScenarioForm
          propertyId={shareToken}
          property={propertyWithAnalysis}
          initialScenario={editingScenario}
          advancedExpenses={advancedExpenses}
          onBack={() => { setViewMode('dashboard'); setEditingScenario(null); }}
          onSaved={() => { setViewMode('dashboard'); setEditingScenario(null); }}
          onSaveScenario={(data) => saveShareScenario(shareToken, data, creatorInfo)}
          onUpdateScenario={(id, data) => updateShareScenario(shareToken, id, data)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {shareData?.pocketDealMode && currentUser && pocketDealLinkEmailMismatch && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-amber-950 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="flex-1 min-w-0">{t('home.pocketDealLinkEmailMismatchBanner')}</p>
          <button
            type="button"
            className="shrink-0 text-amber-900 underline font-medium hover:no-underline text-left sm:text-right"
            onClick={() => setPocketDealLinkEmailMismatch(false)}
          >
            {t('close')}
          </button>
        </div>
      )}
      {!currentUser && shareToken && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 flex flex-wrap items-center justify-center gap-2 text-sm">
          <span className="text-blue-800">{t('sharedView.addToDashboard')}</span>
          <Link
            to={`/login?share=${shareToken}${pocketAuthQuerySuffix}`}
            className="text-blue-700 font-medium underline hover:no-underline"
          >
            {t('auth.login.link')}
          </Link>
          <span className="text-blue-700">|</span>
          <Link
            to={`/signup?share=${shareToken}${pocketAuthQuerySuffix}`}
            className="text-blue-700 font-medium underline hover:no-underline"
          >
            {t('auth.createAccount')}
          </Link>
        </div>
      )}

      {viewMode === 'projectDocuments' && shareData?.propertyId ? (
        <ProjectDocumentsPage
          property={{ ...propertyWithAnalysis, id: shareData.propertyId, uid: shareData.uid }}
          onBack={() => setViewMode('dashboard')}
          readOnly={true}
        />
      ) : (viewMode !== 'report' || !selectedScenario) && viewMode !== 'amortization' ? (
        <BuildingDashboard
          property={propertyWithAnalysis}
          pocketListingLayout={!!shareData?.pocketDealMode}
          staticScenarios={isWrite ? null : baseScenarios}
          readOnly={!isWrite}
          shareToken={shareToken}
          shareData={shareData}
          baseScenarios={isWrite ? baseScenarios : null}
          shareCreatorInfo={isWrite ? creatorInfo : null}
          shareFilterByCreatorUid={isWrite && currentUser ? currentUser.uid : null}
          onCreateScenario={isWrite ? () => setViewMode('create') : undefined}
          onEditScenario={isWrite ? (sc) => { setEditingScenario(sc); setViewMode('edit'); } : undefined}
          onViewScenario={(sc) => { setSelectedScenario(sc); setViewMode('report'); }}
          onBack={onBack}
          onRemoveFromSharedDashboard={onRemoveFromSharedDashboard}
          shareAccessBannerText={showShareAccessBanner ? bannerText : null}
          onOpenProjectDocuments={shareData?.propertyId ? () => setViewMode('projectDocuments') : undefined}
          clients={[]}
          isCourtierHypo={false}
          onOpenDealFromChat={handleDealFromChat}
        />
      ) : viewMode === 'amortization' && amortizationData ? (
        <div className="p-4">
          <AmortizationPage
            analysis={amortizationData.analysis || analysis}
            currentProperty={amortizationData.property || propertyWithAnalysis}
            scenario={amortizationData.scenario}
            scenarioAnalysis={amortizationData.scenarioAnalysis}
            setCurrentStep={(step) => {
              if (step === 'report' || step === 'dashboard') {
                setAmortizationData(null);
                setViewMode('report');
              }
            }}
          />
        </div>
      ) : (
        <div className="p-4">
          <PropertyReport
            currentProperty={propertyWithAnalysis}
            setCurrentStep={(step) => step === 'dashboard' && (setSelectedScenario(null), setViewMode('dashboard'))}
            analysis={analysis}
            onSave={() => {}}
            advancedExpenses={advancedExpenses}
            scenario={selectedScenario}
            onViewAmortization={(property, analysisData, scenarioData, scenarioAnalysis) => {
              setAmortizationData({ property, analysis: analysisData, scenario: scenarioData, scenarioAnalysis });
              setViewMode('amortization');
            }}
            shareToken={shareToken}
            shareFilterByCreatorUid={currentUser?.uid || null}
            baseScenarios={baseScenarios}
            shareCreatorInfo={creatorInfo}
            readOnly={!isWrite}
            allowSubScenariosEdit={allowSubScenariosEdit}
          />
        </div>
      )}
    </div>
  );
};

export default SharedPropertyView;
